import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { TABLIST, TABLIST_ARRAY, TABLIST_TEXT_MAPPING } from "./contants";
import styles from "./CGMDetails.module.scss";
import { useParams } from "react-router-dom";
import { fetchAndActivate, getValue } from "firebase/remote-config";
import { remoteConfig } from "../../../utils/firebase";
import DateChangeComponent from "../common/DateChangeComponent";
import moment from "moment";
import { cgmService } from "../../../api/services";
import makeRequest from "../../../api";
import UserAnomalies from "./Anomalies";
import { toast } from "react-toastify";

const UserProfile = React.lazy(() => import("./UserProfile"));
const DeviceData = React.lazy(() => import("./DeviceData"));

const CGMDetailsComponent = () => {
  const params = useParams();
  const userId = params.userId;

  const date = moment()
    .set({
      hour: 0,
      minutes: 0,
      seconds: 0,
      milliseconds: 0,
    })
    .valueOf();

  const [currentTab, setCurrentTab] = useState(TABLIST.USER_PROFILE);
  const [userDetails, setUserDetails] = useState<any>(null);
  const [currentDate, setCurrentDate] = useState<any>(date);

  const [cgmData, setCGMData] = useState<any>([]);
  const [glucose, setGlucose] = useState<any>(null);
  const [anomaliesList, setAnomaliesList] = useState<any>([]);
  const [fetchingCGMData, setFetchingCGMData] = useState(false);
  const [anomaliesTimestamp, setAnomaliesTimestamp] = useState<any>(null);

  useEffect(() => {
    if (userId) {
      getUserData();
    }
  }, [userId]);

  useEffect(() => {
    if (userId && currentDate) {
      setFetchingCGMData(true);
      getCGMDeviceData();
    }
  }, [userId, currentDate]);

  useEffect(() => {
    const fetchRemoteConfig = () => {
      fetchAndActivate(remoteConfig).then(() => {
        const res: any = getValue(remoteConfig, "cgm_config_v1").asString();
        const data = JSON.parse(res);
        const anomalies = getCurrentActiveItem(data?.anomalies)?.algo;
        const glucoseValues = getCurrentActiveItem(data?.glucose);
        const myfn = new Function("return " + anomalies)();
        const myArray = cgmData?.map((item: any) => ({
          glucose: item?.glucoseValue,
          timestamp: item?.recordedAt,
          ...item,
        }));
        const annnn = JSON.parse(
          myfn(
            JSON.stringify(myArray),
            glucoseValues.lower,
            glucoseValues.upper
          )
        );

        const anomaliesObj: any = {};
        annnn?.forEach((item: any) => {
          const minutes = moment(item?.recordedAt).diff(
            currentDate.valueOf(),
            "minutes"
          );
          anomaliesObj[minutes] = 1;
        });
        setAnomaliesTimestamp({ ...anomaliesObj });

        setAnomaliesList([...annnn]);

        setGlucose({
          lower: glucoseValues.lower,
          upper: glucoseValues.upper,
        });
      });
    };

    fetchRemoteConfig();
  }, [currentDate, cgmData]);

  const handleChangeCurrentDate = (action: string) => {
    if (action === "add") {
      if (currentDate < date) {
        setCurrentDate(moment(currentDate).add("1", "day"));
      }
    } else {
      setCurrentDate(moment(currentDate).subtract("1", "day"));
    }
  };

  const getCurrentActiveItem = (list: any) => {
    const currentTimeStamp = currentDate.valueOf();
    return (
      list.find(
        (item: any) =>
          (item.start_time <= currentTimeStamp &&
            item.end_time > currentTimeStamp) ||
          (item.start_time <= currentTimeStamp && item.end_time === -1)
      ) || null
    );
  };

  const getCGMDeviceData = async () => {
    try {
      const res = await makeRequest(
        cgmService.getUserCGMDeviceData(
          {
            startTs: currentDate.valueOf(),
            userId,
          },
          userId
        )
      );
      const response: any = res?.data?.body?.dataPoints;
      setCGMData([...response]);
      setFetchingCGMData(false);
    } catch (error: any) {
      toast.error("Error while fetching user's CGM device data");
    }
  };

  const getUserData = async () => {
    try {
      const res = await makeRequest(
        cgmService.getCGMUserDetails(
          {
            userId,
          },
          userId
        )
      );
      setUserDetails({ ...res?.data?.body });
      const lastScannedValue = res?.data?.body?.lastScannedAt
        ? moment(res?.data?.body?.lastScannedAt)
            .set({
              hour: 0,
              minutes: 0,
              seconds: 0,
              milliseconds: 0,
            })
            .valueOf()
        : date;
      setCurrentDate(lastScannedValue);
    } catch (error: any) {
      toast.error("Error while fetching user details");
    }
  };

  const handleTabChange = (e: React.MouseEvent<HTMLDivElement>) => {
    const targetElement = e.currentTarget as HTMLDivElement;
    e.stopPropagation();
    if (!targetElement) {
      return;
    }
    const tab = targetElement.getAttribute("data-tab");
    if (!tab) return;

    setCurrentTab(tab);
  };

  const renderCurrentTabComponent = () => {
    switch (currentTab) {
      case TABLIST.USER_PROFILE:
        return <UserProfile data={userDetails} />;
      case TABLIST.CGM_DATA:
        return (
          <DeviceData
            cgmData={cgmData}
            date={currentDate}
            glucose={glucose}
            anomaliesMap={anomaliesTimestamp}
          />
        );
      case TABLIST.ANOMALIES:
        return (
          <UserAnomalies
            anomaliesList={anomaliesList}
            getCGMData={getCGMDeviceData}
          />
        );
      default:
        return <></>;
    }
  };

  const renderTabList = useMemo(() => {
    return TABLIST_ARRAY.map((tab: string, index: number) => {
      return (
        <div className={styles.tabListBtnWrapper} key={`tab-${index}`}>
          <div
            className={`${styles.tabListBtn} ${
              currentTab === tab ? styles.tabListActiveBtn : ""
            }`}
            data-tab={tab}
            key={tab}
            onClick={handleTabChange}
          >
            {TABLIST_TEXT_MAPPING[tab]}
          </div>
          {currentTab === tab && <div className={styles.bottomListActiveBtn} />}
        </div>
      );
    });
  }, [currentTab]);

  return (
    <div className="container py-0 my-0">
      <div className={styles.headerTextWrapper}>
        <h2 className="fw-600 fs-32">
          {userDetails?.userName || "Anonymous user"}'s data
        </h2>
        <DateChangeComponent
          handleDateChange={handleChangeCurrentDate}
          date={currentDate}
        />
      </div>
      <div className={styles.tabListWrapper}>{renderTabList}</div>
      <div className={styles.contentWrapper}>
        {fetchingCGMData ? (
          <div className="fs-18 fw-700">Loading...</div>
        ) : (
          <Suspense fallback={<div>Loading...</div>}>
            {renderCurrentTabComponent()}
          </Suspense>
        )}
      </div>
    </div>
  );
};

export default CGMDetailsComponent;
